import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ClipboardCheck, PlaneTakeoff, Loader2, RotateCcw } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";

// Sensible fallback if the customer has not (yet) saved a personal list.
// Mirrors backend `DEFAULT_DEPARTURE_CHECKLIST` / `DEFAULT_ARRIVAL_CHECKLIST`.
const DEFAULT_DEPARTURE_DE = [
  { label: "Alle Fenster und Türen geschlossen" },
  { label: "Alarmanlage aktiviert" },
  { label: "Müll entsorgt" },
  { label: "Kühlschrank kontrolliert · Verderbliches entfernt" },
  { label: "Wasserzufuhr abgestellt" },
  { label: "Heizung / Klima auf Abwesenheits-Modus" },
  { label: "Kaffeemaschine · Elektrogeräte ausgeschaltet" },
  { label: "Rollläden heruntergelassen" },
];
const DEFAULT_DEPARTURE_EN = [
  { label: "All windows and doors closed" },
  { label: "Alarm system armed" },
  { label: "Rubbish taken out" },
  { label: "Fridge checked · perishables removed" },
  { label: "Water supply turned off" },
  { label: "Heating / A/C in away mode" },
  { label: "Coffee machine & appliances switched off" },
  { label: "Shutters closed" },
];
const DEFAULT_ARRIVAL_DE = [
  { label: "Heizung auf Wohntemperatur eingestellt" },
  { label: "Rollläden geöffnet · Räume gelüftet" },
  { label: "Frische Lebensmittel bereitgestellt" },
  { label: "Wasserzufuhr geöffnet · Boiler eingeschaltet" },
  { label: "Alarmanlage deaktiviert" },
  { label: "Blumen · Willkommensgruß platziert" },
];
const DEFAULT_ARRIVAL_EN = [
  { label: "Heating set to living temperature" },
  { label: "Shutters opened · rooms aired" },
  { label: "Fresh groceries prepared" },
  { label: "Water supply on · boiler heated up" },
  { label: "Alarm disarmed" },
  { label: "Flowers · welcome touch placed" },
];

const uid = () => `item-${Math.random().toString(36).slice(2, 10)}`;

/**
 * "Meine Abreise-Checkliste" — customer edits their personal default list.
 * Lists are stored per customer in `customer_checklists` (backend) and are
 * auto-loaded into every new absence booking on POST /bookings.
 */
export default function MyChecklistSection({ propertyId, propertyName }) {
  const { lang } = useI18n();
  const de = lang === "de";
  const [dep, setDep] = useState([]);
  const [arr, setArr] = useState([]);
  const [orig, setOrig] = useState({ items: [], arrival_items: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Feb 2026 · 8 Sep 2026 · User-Directive „Checkliste pro Objekt":
  // `propertyId` als Query-Param anhängen. Backend fällt auf Legacy-Liste
  // zurück, wenn für dieses Objekt noch keine eigene existiert.
  const qs = propertyId ? `?property_id=${encodeURIComponent(propertyId)}` : "";

  useEffect(() => {
    const qs = propertyId ? `?property_id=${encodeURIComponent(propertyId)}` : "";
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/me/checklist${qs}`);
        const items = Array.isArray(data?.items) ? data.items : [];
        const arrivals = Array.isArray(data?.arrival_items) ? data.arrival_items : [];
        const DEP_DEFAULTS = de ? DEFAULT_DEPARTURE_DE : DEFAULT_DEPARTURE_EN;
        const ARR_DEFAULTS = de ? DEFAULT_ARRIVAL_DE : DEFAULT_ARRIVAL_EN;
        setDep(items.length ? items : DEP_DEFAULTS.map((x) => ({ ...x, key: uid() })));
        setArr(arrivals.length ? arrivals : ARR_DEFAULTS.map((x) => ({ ...x, key: uid() })));
        setOrig({ items, arrival_items: arrivals });
      } catch (err) {
        toast.error(formatErr(err.response?.data?.detail) || err.message);
      } finally { setLoading(false); }
    })();
  }, [propertyId, de]);

  const dirty = useMemo(() => {
    const norm = (arr) => JSON.stringify(arr.map((x) => (x.label || "").trim()).filter(Boolean));
    return norm(dep) !== norm(orig.items) || norm(arr) !== norm(orig.arrival_items);
  }, [dep, arr, orig]);

  const save = async () => {
    setSaving(true);
    try {
      const items = dep.filter((x) => (x.label || "").trim());
      const arrival_items = arr.filter((x) => (x.label || "").trim());
      const { data } = await api.put(`/me/checklist${qs}`, { items, arrival_items });
      setOrig({ items: data.items || [], arrival_items: data.arrival_items || [] });
      toast.success(de
        ? "Checkliste gespeichert · wird bei neuen Abwesenheiten automatisch geladen."
        : "Checklist saved · will be loaded automatically for new absences.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const resetToDefaults = () => {
    const DEP_DEFAULTS = de ? DEFAULT_DEPARTURE_DE : DEFAULT_DEPARTURE_EN;
    const ARR_DEFAULTS = de ? DEFAULT_ARRIVAL_DE : DEFAULT_ARRIVAL_EN;
    setDep(DEP_DEFAULTS.map((x) => ({ ...x, key: uid() })));
    setArr(ARR_DEFAULTS.map((x) => ({ ...x, key: uid() })));
  };

  return (
    <section
      className="bg-white border border-navy/10 p-6 md:p-8 space-y-8"
      data-testid="my-checklist-section"
    >
      <div>
        <div className="flex items-center gap-2 mb-2">
          <ClipboardCheck className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
          <h2 className="font-display text-2xl text-navy">
            {de ? "Meine Abwesenheitscheckliste" : "My departure checklist"}
            {propertyName && (
              <span className="ml-2 text-[13px] tracking-wider uppercase text-navy-dark/50 not-italic">
                · {propertyName}
              </span>
            )}
          </h2>
        </div>
        <p className="text-[13px] text-navy-dark/70 leading-relaxed">
          {de ? (
            <>
              Erstellen Sie eine Vorlage mit persönlichen TO-DOs für die Zeit vor jeder Ab- und Anreise. Diese Checkliste wird bei jeder neuen Abwesenheit automatisch in der Abwesenheitskachel angezeigt.{" "}
              <span className="italic">Nur für Kunden einsehbar.</span>
            </>
          ) : (
            "Your personal to-dos for every departure. This list is loaded automatically into every new absence tile."
          )}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-navy-dark/60 text-[13px]">
          <Loader2 className="w-4 h-4 animate-spin" /> {de ? "Wird geladen …" : "Loading …"}
        </div>
      ) : (
        <>
          <ChecklistEditor
            icon={PlaneTakeoff}
            title={de ? "Abreise" : "Departure"}
            items={dep}
            setItems={setDep}
            placeholder={de ? "z. B. Katze zur Pflegestelle bringen" : "e.g. Drop the cat off at the sitter"}
            testid="checklist-editor-departure"
            de={de}
          />
          <ChecklistEditor
            icon={ClipboardCheck}
            title={de ? "Anreise" : "Return"}
            items={arr}
            setItems={setArr}
            placeholder={de ? "z. B. Sauna vorheizen" : "e.g. Preheat the sauna"}
            testid="checklist-editor-arrival"
            de={de}
          />

          <div className="flex items-center justify-between gap-3 pt-4 border-t border-navy/10 flex-wrap">
            <button
              type="button"
              onClick={resetToDefaults}
              disabled={saving}
              data-testid="checklist-reset-defaults-btn"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase text-navy-dark/70 hover:text-navy px-3 py-2 border border-navy/15 hover:border-navy/40 transition-colors disabled:opacity-40"
            >
              <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.6} /> {de ? "Standard wiederherstellen" : "Restore defaults"}
            </button>
            <div className="flex items-center gap-3">
              {dirty && (
                <span className="text-[11px] tracking-wider uppercase" style={{ color: BRAND_COLORS.gold }}>
                  · {de ? "Ungespeicherte Änderungen" : "Unsaved changes"}
                </span>
              )}
              <button
                type="button"
                onClick={save}
                disabled={!dirty || saving}
                data-testid="checklist-save-btn"
                className="inline-flex items-center gap-2 text-ivory px-6 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-40"
                style={{ background: BRAND_COLORS.navy }}
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                {de ? "Speichern" : "Save"}
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function ChecklistEditor({ icon: Icon, title, items, setItems, placeholder, testid, de }) {
  const [draft, setDraft] = useState("");

  const add = () => {
    const label = draft.trim();
    if (!label) return;
    setItems((cur) => [...cur, { key: uid(), label }]);
    setDraft("");
  };
  const removeAt = (idx) => setItems((cur) => cur.filter((_, i) => i !== idx));
  const editAt = (idx, label) =>
    setItems((cur) => cur.map((it, i) => (i === idx ? { ...it, label } : it)));

  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.5} />
        <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>{title}</div>
        <div className="ml-auto label-caps text-navy-dark/50">{items.length} {de ? (items.length === 1 ? "Punkt" : "Punkte") : (items.length === 1 ? "item" : "items")}</div>
      </div>

      <ul className="space-y-1.5">
        {items.map((it, idx) => (
          <li
            key={it.key || idx}
            className="flex items-center gap-2 group"
            data-testid={`${testid}-row-${idx}`}
          >
            <span className="w-4 h-4 border rounded flex items-center justify-center shrink-0" style={{ borderColor: withAlpha(BRAND_COLORS.navy, 0.25), background: "#fff" }} aria-hidden />
            <input
              type="text"
              value={it.label}
              onChange={(e) => editAt(idx, e.target.value)}
              className="flex-1 bg-ivory/40 border border-transparent hover:border-navy/15 focus:border-gold focus:bg-white focus:outline-none px-2 py-1.5 text-[13px] text-navy-dark"
              data-testid={`${testid}-input-${idx}`}
            />
            <button
              type="button"
              onClick={() => removeAt(idx)}
              aria-label={de ? "Punkt löschen" : "Delete item"}
              data-testid={`${testid}-delete-${idx}`}
              className="w-8 h-8 flex items-center justify-center text-navy-dark/40 hover:text-red-700 hover:bg-red-50 transition-colors border border-transparent hover:border-red-200"
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
          </li>
        ))}
      </ul>

      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-navy/5">
        <Plus className="w-3.5 h-3.5 text-navy-dark/40" strokeWidth={1.5} />
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="flex-1 bg-transparent border-b border-navy/15 focus:border-gold focus:outline-none px-1 py-1.5 text-[13px] text-navy-dark italic placeholder:text-navy-dark/40"
          data-testid={`${testid}-add-input`}
        />
        <button
          type="button"
          onClick={add}
          disabled={!draft.trim()}
          data-testid={`${testid}-add-btn`}
          className="text-[11px] tracking-widest uppercase text-navy-dark/70 hover:text-navy px-2 py-1 disabled:opacity-30"
        >
          {de ? "Hinzufügen" : "Add"}
        </button>
      </div>
    </div>
  );
}
